import {RefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {BellIcon, FiltersIcon, SearchIcon, SortIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {BUTTONS_TEST_ID, NAVIGATION_MOBILE_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {SearchTab} from "../types/SearchTab";
import {ISearchInputValue} from "./ISearchInputValue";
import {RegionPillsOverflow} from "./RegionPillsOverflow";

interface IProps {
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: RefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    sortActive?: boolean;
    isAbroad?: boolean;
    disableAlert?: boolean;
    showLocationSearch?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
}

const mobileSearchTriggersWrapperHeight = 64;

export const MobileTopSearchTriggers = (props: IProps) => {
    const {
        latestQuery,
        onToggleFiltersClick,
        onToggleSortClick,
        selectedFiltersCount,
        disableAlert,
        firstSelectedRegion,
        isAbroad,
        onMount,
        onToggleSearchClick,
        searchFieldPropsValue,
        showLocationSearch,
        sortActive
    } = props;

    const dispatch = useDispatch();

    const searchTriggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onMount && onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        onToggleSortClick && onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionLabels = useMemo(() => {
        if (!searchFieldPropsValue) {
            return firstSelectedRegion ? [firstSelectedRegion] : [];
        }

        if (searchFieldPropsValue.tabType === SearchTab.Regions) {
            return searchFieldPropsValue.regions.map((region) => region.name);
        }

        return [];
    }, [firstSelectedRegion, searchFieldPropsValue]);

    return (
        <div className={wrapper}>
            <div className={triggersHolder}>
                {!isAbroad && showLocationSearch && (
                    <div className={inputWrapper} onClick={onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {regionLabels.length > 0 ? (
                            <MobileTopSearchRegionPills regionLabels={regionLabels} />
                        ) : (
                            <Text noWrap className={inputWrapperText} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}

                <div className={triggerIconHolder} onClick={onToggleFiltersClick} data-testid={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                    <Text variant="info_txt_2" className={gapTop}>
                        filtruj
                    </Text>

                    {selectedFiltersCount ? <div className={triggerBadge}>{selectedFiltersCount}</div> : ""}
                </div>

                <div className={triggerIconHolder} onClick={onSortClick} data-testid={NAVIGATION_TEST_IDS.SORT_ICON}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}
                    />

                    <Text variant="info_txt_2" className={gapTop} color={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}>
                        sortuj
                    </Text>
                </div>

                {latestQuery?.region && !isAbroad && !disableAlert && (
                    <NewsletterFlowWithRegistrationTrigger
                        amplitudePlacement="rp_save_alert_search_bar_top"
                        customButton={(props) => (
                            <div className={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                                <Text variant="info_txt_2" className={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

const MobileTopSearchRegionPills = ({regionLabels}: {regionLabels: string[]}) => {
    return (
        <RegionPillsOverflow
            regionLabels={regionLabels}
            wrapperClassName={inputPillsWrapper}
            dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}
            renderPill={(regionPillLabel, {isVisible, index}) => (
                <MobileTopSearchRegionPill key={`${regionPillLabel}-${index.toString()}`} label={regionPillLabel} isVisible={isVisible} />
            )}
        />
    );
};

const MobileTopSearchRegionPill = ({isVisible, label}: {isVisible?: boolean; label: string}) => {
    return (
        <Pill size="small" className={inputPill}>
            <Text noWrap className={inputWrapperText}>
                {isVisible ? <span data-visible-region-pill-label>{label}</span> : label}
            </Text>
        </Pill>
    );
};

const wrapper = css({
    background: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const commonTriggersHolder = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "0 auto",
    width: "100%"
};

const triggersHolder = css({
    ...commonTriggersHolder,
    gap: 1.5,

    xs: {
        gap: 3
    }
});

const triggerIconHolder = css({
    color: "var(--colors-secondary)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",

    "&:visited, &:hover, &:focus, &:active": {
        color: "var(--colors-secondary)",
        textDecoration: "none"
    }
});

const triggerBadge = css({
    position: "absolute",
    top: 0,
    right: 0,
    color: "var(--colors-white)",
    background: "var(--colors-danger)",
    fontSize: "var(--globals-text-root)",
    borderRadius: "50%",
    border: "1px solid var(--colors-white)",
    width: "1.6rem",
    height: "1.6rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "translateX(50%)"
});

const inputWrapper = css({
    display: "flex",
    justifyContent: "normal",
    alignItems: "center",
    boxShadow: "var(--shadow-2)",
    border: "1px solid var(--colors-gray-600)",
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
    mr: -1,
    p: 1,
    px: 2,
    gap: 1,
    borderRadius: 1
});

const inputPillsWrapper = css({
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    flexWrap: "nowrap",
    gap: 0.5
});

const inputPill = css({
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: "100%"
});

const inputWrapperText = css({
    pr: 1,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "100%"
});

const gapTop = css({
    mt: 0.5
});
import {RefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch} from "react-redux";
import {css} from "@panda/css";

import {BellIcon, FiltersIcon, SearchIcon, SortIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {BUTTONS_TEST_ID, NAVIGATION_MOBILE_TEST_IDS, NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {NewsletterFlowWithRegistrationTrigger} from "../../newsletter/components/NewsletterFlowWithRegistrationTrigger";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {SearchTab} from "../types/SearchTab";
import {ISearchInputValue} from "./ISearchInputValue";
import {RegionPillsOverflow} from "./RegionPillsOverflow";

interface IProps {
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: RefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    sortActive?: boolean;
    isAbroad?: boolean;
    disableAlert?: boolean;
    showLocationSearch?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
}

const mobileSearchTriggersWrapperHeight = 64;

export const MobileTopSearchTriggers = (props: IProps) => {
    const {
        latestQuery,
        onToggleFiltersClick,
        onToggleSortClick,
        selectedFiltersCount,
        disableAlert,
        firstSelectedRegion,
        isAbroad,
        onMount,
        onToggleSearchClick,
        searchFieldPropsValue,
        showLocationSearch,
        sortActive
    } = props;

    const dispatch = useDispatch();

    const searchTriggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onMount && onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        onToggleSortClick && onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionLabels = useMemo(() => {
        if (!searchFieldPropsValue) {
            return firstSelectedRegion ? [firstSelectedRegion] : [];
        }

        if (searchFieldPropsValue.tabType === SearchTab.Regions) {
            return searchFieldPropsValue.regions.map((region) => region.name);
        }

        return [];
    }, [firstSelectedRegion, searchFieldPropsValue]);

    return (
        <div className={wrapper}>
            <div className={triggersHolder}>
                {!isAbroad && showLocationSearch && (
                    <div className={inputWrapper} onClick={onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {regionLabels.length > 0 ? (
                            <MobileTopSearchRegionPills regionLabels={regionLabels} />
                        ) : (
                            <Text noWrap className={inputWrapperText} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}

                <div className={triggerIconHolder} onClick={onToggleFiltersClick} data-testid={BUTTONS_TEST_ID.OPEN_FILTERS_BUTTON}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                    <Text variant="info_txt_2" className={gapTop}>
                        filtruj
                    </Text>

                    {selectedFiltersCount ? <div className={triggerBadge}>{selectedFiltersCount}</div> : ""}
                </div>

                <div className={triggerIconHolder} onClick={onSortClick} data-testid={NAVIGATION_TEST_IDS.SORT_ICON}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}
                    />

                    <Text variant="info_txt_2" className={gapTop} color={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}>
                        sortuj
                    </Text>
                </div>

                {latestQuery?.region && !isAbroad && !disableAlert && (
                    <NewsletterFlowWithRegistrationTrigger
                        amplitudePlacement="rp_save_alert_search_bar_top"
                        customButton={(props) => (
                            <div className={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                                <Text variant="info_txt_2" className={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

const MobileTopSearchRegionPills = ({regionLabels}: {regionLabels: string[]}) => {
    return (
        <RegionPillsOverflow
            regionLabels={regionLabels}
            wrapperClassName={inputPillsWrapper}
            dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}
            renderPill={(regionPillLabel, {isVisible, index}) => (
                <MobileTopSearchRegionPill key={`${regionPillLabel}-${index.toString()}`} label={regionPillLabel} isVisible={isVisible} />
            )}
        />
    );
};

const MobileTopSearchRegionPill = ({isVisible, label}: {isVisible?: boolean; label: string}) => {
    return (
        <Pill size="small" className={inputPill}>
            <Text noWrap className={inputWrapperText}>
                {isVisible ? <span data-visible-region-pill-label>{label}</span> : label}
            </Text>
        </Pill>
    );
};

const wrapper = css({
    background: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const commonTriggersHolder = {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "0 auto",
    width: "100%"
};

const triggersHolder = css({
    ...commonTriggersHolder,
    gap: 1.5,

    xs: {
        gap: 3
    }
});

const triggerIconHolder = css({
    color: "var(--colors-secondary)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",

    "&:visited, &:hover, &:focus, &:active": {
        color: "var(--colors-secondary)",
        textDecoration: "none"
    }
});

const triggerBadge = css({
    position: "absolute",
    top: 0,
    right: 0,
    color: "var(--colors-white)",
    background: "var(--colors-danger)",
    fontSize: "var(--globals-text-root)",
    borderRadius: "50%",
    border: "1px solid var(--colors-white)",
    width: "1.6rem",
    height: "1.6rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "translateX(50%)"
});

const inputWrapper = css({
    display: "flex",
    justifyContent: "normal",
    alignItems: "center",
    boxShadow: "var(--shadow-2)",
    border: "1px solid var(--colors-gray-600)",
    width: "100%",
    minWidth: 0,
    flex: "1 1 auto",
    mr: -1,
    p: 1,
    px: 2,
    gap: 1,
    borderRadius: 1
});

const inputPillsWrapper = css({
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    flexWrap: "nowrap",
    gap: 0.5
});

const inputPill = css({
    flex: "0 1 auto",
    minWidth: 0,
    maxWidth: "100%"
});

const inputWrapperText = css({
    pr: 1,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "100%"
});

const gapTop = css({
    mt: 0.5
});
